// PhotoCard.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Utilities
import { getNodeSubtitle } from 'utilities/NodeSubtitle';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// CSS
import './PhotoCard.css';

/**
 * PhotoCard
 *
 * Displays a photo node in the feed.
 * Falls back to node.url if summaries contain only storage paths.
 *
 * @param {object} node - The photo node to display.
 *
 * @returns {ReactNode} The rendered photo feed node or null if invalid input.
 */
const PhotoCard = ({ node }) => {

    // ----- STATE ----- //

    const [imageUrl, setImageUrl] = useState(null);

    // ----- MEMOS ----- //

    // Prefer the original over derivatives. Feed thumbs can disagree with the
    // browser-oriented original (EXIF rotation), which looks like stretching.
    const candidateUrls = useMemo(() => (
        [node?.url, node?.url_feed, node?.url_grid, node?.url_thumb]
            .filter((value, index, arr) => typeof value === 'string' && value.trim() && arr.indexOf(value) === index)
    ), [node?.url, node?.url_feed, node?.url_grid, node?.url_thumb]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const nodeKey = node?.key || null;

            for (const rawUrl of candidateUrls) {
                const url = await resolveImageUrl({ nodeKey, url: rawUrl, storagePath: null });
                if (cancelled) return;
                if (url) {
                    setImageUrl(url);
                    return;
                }
            }

            if (node?.storagePath) {
                const fallback = await resolveImageUrl({ nodeKey, url: null, storagePath: node.storagePath });
                if (cancelled) return;
                setImageUrl(fallback || null);
                return;
            }

            setImageUrl(null);
        })();

        return () => {
            cancelled = true;
        };
    }, [node?.key, node?.storagePath, candidateUrls]);

    // Don't render if no node
    if (!node) return null;

    if (!imageUrl) return null;

    // ----- RENDER ----- //

    return (
        <div className="photo-card-container">

            {/* IMAGE */}
            <img
              className="photo-feed-card-image"
              src={imageUrl}
              alt={getNodeSubtitle(node) || 'Photo'}
            />

        </div>
    );
};

// Dev Tools
PhotoCard.displayName = 'PhotoCard';

export default PhotoCard;
